import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Copy, CheckCheck, FileCode2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { marked } from "marked";
import { toast } from "sonner";
import {
  PageRoot,
  PageContainer,
  PageHeader,
  PageTitle,
  PageDescription,
  PageContent,
} from "@/components/ui/page";

import components from "@/data/components";

// Component documentation section
const ComponentDocumentation = ({ component }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [copied, setCopied] = useState(false);

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast("Code copied to clipboard");
  };

  if (!component) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px] border border-dashed border-border rounded-lg">
        <div className="text-center p-8">
          <FileCode2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">Select a component</h3>
          <p className="text-muted-foreground max-w-md">
            Choose a component from the list to view its documentation,
            examples, and usage code.
          </p>
        </div>
      </div>
    );
  }

  const PreviewComponent = component.preview;

  return (
    <div className="space-y-8">
      {/* Component header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b">
        <div>
          <h2 className="text-2xl font-bold flex items-center">
            {component.icon}
            {component.title}
          </h2>
          <p className="text-muted-foreground mt-1">{component.description}</p>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="flex border-b overflow-x-auto">
        <button
          className={cn(
            "px-4 py-2 font-medium text-sm transition-colors whitespace-nowrap",
            activeTab === "overview"
              ? "border-b-2 border-primary text-foreground"
              : "text-muted-foreground hover:text-foreground"
          )}
          onClick={() => setActiveTab("overview")}
        >
          Overview
        </button>
        <button
          className={cn(
            "px-4 py-2 font-medium text-sm transition-colors whitespace-nowrap",
            activeTab === "examples"
              ? "border-b-2 border-primary text-foreground"
              : "text-muted-foreground hover:text-foreground"
          )}
          onClick={() => setActiveTab("examples")}
        >
          Examples
        </button>
        <button
          className={cn(
            "px-4 py-2 font-medium text-sm transition-colors whitespace-nowrap",
            activeTab === "props"
              ? "border-b-2 border-primary text-foreground"
              : "text-muted-foreground hover:text-foreground"
          )}
          onClick={() => setActiveTab("props")}
        >
          API
        </button>
        <button
          className={cn(
            "px-4 py-2 font-medium text-sm transition-colors whitespace-nowrap",
            activeTab === "source"
              ? "border-b-2 border-primary text-foreground"
              : "text-muted-foreground hover:text-foreground"
          )}
          onClick={() => setActiveTab("source")}
        >
          Source Code
        </button>
        <button
          className={cn(
            "px-4 py-2 font-medium text-sm transition-colors whitespace-nowrap",
            activeTab === "installation"
              ? "border-b-2 border-primary text-foreground"
              : "text-muted-foreground hover:text-foreground"
          )}
          onClick={() => setActiveTab("installation")}
        >
          Installation
        </button>
      </div>

      {/* Tab content */}
      <div className="py-4">
        {activeTab === "overview" && (
          <div className="space-y-8">
            {/* Preview */}
            <div className="border border-border rounded-xl overflow-hidden">
              <div className="bg-muted p-4 border-b border-border">
                <h3 className="font-medium">Preview</h3>
              </div>
              <div className="p-8 flex justify-center items-center min-h-[200px] bg-background">
                <PreviewComponent />
              </div>
            </div>

            {/* Code */}
            <div className="border border-border rounded-xl overflow-hidden">
              <div className="bg-muted p-4 flex justify-between items-center border-b border-border">
                <h3 className="font-medium">Basic Code</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyCode(component.code)}
                >
                  {copied ? (
                    <CheckCheck className="h-4 w-4 mr-2" />
                  ) : (
                    <Copy className="h-4 w-4 mr-2" />
                  )}
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
              <div className="p-4 bg-muted/50 overflow-auto max-h-96">
                <pre className="text-sm font-mono">
                  <code>{component.code}</code>
                </pre>
              </div>
            </div>
          </div>
        )}

        {activeTab === "examples" && (
          <div className="space-y-8">
            {component.examples.map((example, index) => (
              <div
                key={index}
                className="border border-border rounded-xl overflow-hidden"
              >
                <div className="bg-muted p-4 flex justify-between items-center border-b border-border">
                  <h3 className="font-medium">{example.title}</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyCode(example.code)}
                  >
                    {copied ? (
                      <CheckCheck className="h-4 w-4 mr-2" />
                    ) : (
                      <Copy className="h-4 w-4 mr-2" />
                    )}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </div>
                <div className="p-4 bg-muted/50 overflow-auto">
                  <pre className="text-sm font-mono">
                    <code>{example.code}</code>
                  </pre>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === "props" && (
          <div className="border border-border rounded-xl overflow-hidden">
            <div className="bg-muted p-4 border-b border-border">
              <h3 className="font-medium">Props</h3>
            </div>
            <div className="divide-y divide-border">
              {component.props.map((prop, index) => (
                <div key={index} className="p-4">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <code className="font-mono text-sm bg-muted px-1 py-0.5 rounded">
                      {prop.name}
                    </code>
                    <span className="text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-2 py-0.5 rounded-full">
                      {prop.type}
                    </span>
                    {prop.default && (
                      <span className="text-xs text-muted-foreground">
                        Default: {prop.default}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {prop.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "source" && (
          <div className="border border-border rounded-xl overflow-hidden">
            <div className="bg-muted p-4 flex justify-between items-center border-b border-border">
              <h3 className="font-medium">Source Code</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyCode(component.sourceCode || "")}
              >
                {copied ? (
                  <CheckCheck className="h-4 w-4 mr-2" />
                ) : (
                  <Copy className="h-4 w-4 mr-2" />
                )}
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
            <div className="p-4 bg-muted/50 overflow-auto max-h-[500px]">
              <pre className="text-sm font-mono">
                <code>
                  {component.sourceCode || "Source code not available"}
                </code>
              </pre>
            </div>
          </div>
        )}

        {activeTab === "installation" && (
          <div className="space-y-4">
            <div className="border border-border rounded-xl overflow-hidden">
              <div className="bg-muted p-4 border-b border-border">
                <h3 className="font-medium">Installation</h3>
              </div>
              <div className="p-6">
                {component.detailedInstallation ? (
                  <div className="prose dark:prose-invert max-w-none prose-pre:bg-muted/70 prose-pre:text-sm prose-pre:p-4 prose-pre:rounded-md">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: marked.parse(component.detailedInstallation),
                      }}
                    />
                  </div>
                ) : (
                  <>
                    <p className="mb-4">{component.installation}</p>
                    {component.installation.includes("already included") && (
                      <p className="text-sm text-muted-foreground">
                        The component is part of the core UI library and doesn't
                        require additional installation steps.
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ComponentsPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedComponent, setSelectedComponent] = useState(null);

  // Filter components based on search
  const filteredComponents = components.filter((component) =>
    component.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Helmet>
        <title>UI Components | Webbyj - Component Documentation</title>
        <meta
          name="description"
          content="Comprehensive documentation for UI components, including examples, API reference, and installation guides."
        />
      </Helmet>
      <PageRoot>
        <PageContainer>
          <PageHeader>
            <PageTitle>UI Components</PageTitle>
            <PageDescription>
              Comprehensive documentation for all UI components in the Webbyj
              framework. Learn how to use each component with examples, API
              references, and installation guides.
            </PageDescription>
          </PageHeader>

          <PageContent>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-10 mb-10">
              {/* Sidebar with component list */}
              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  {/* Search */}
                  <div className="mb-4 relative max-w-md">
                    <Input
                      type="text"
                      placeholder="Search components..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-12 py-6 text-base"
                    />
                    <Search
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                      size={20}
                    />
                  </div>

                  <div className="space-y-1">
                    <h3 className="font-medium mb-2 text-sm text-muted-foreground">
                      Components
                    </h3>
                    {filteredComponents.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-2">
                        No components found
                      </p>
                    ) : (
                      filteredComponents.map((component) => (
                        <Button
                          key={component.id}
                          variant={
                            selectedComponent?.id === component.id
                              ? "default"
                              : "ghost"
                          }
                          className={cn(
                            "w-full justify-start",
                            selectedComponent?.id === component.id
                              ? ""
                              : "text-muted-foreground"
                          )}
                          onClick={() => setSelectedComponent(component)}
                        >
                          {component.icon}
                          {component.title}
                        </Button>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Component documentation */}
              <div className="lg:col-span-3">
                <ComponentDocumentation component={selectedComponent} />
              </div>
            </div>
          </PageContent>
        </PageContainer>
      </PageRoot>
    </>
  );
};

export default ComponentsPage;
